import { useState } from "react";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { Switch } from "@babackapp/ui/components/switch";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@babackapp/ui/components/toggle-group";
import { useCarriersContext } from "../../use-carriers-context";
import {
  FEDEX_ONE_RATE_VALUE,
  FEDEX_PICKUP_DROPOFF,
  FEDEX_PICKUP_SCHEDULED,
  laneOf,
  productsForLane,
  type FedexLane,
  valueToCredentials,
} from "../fedex-plans";

// Dropdown to pick the FedEx service (shipping plan) for a registered FedEx
// carrier: flat-rate One Rate envelope, or a real FedEx service (domestic /
// international). This is a returns product, so duties are never merchant-paid:
// international services always use DAP (recipient), there is no DDP/DAP choice.
// A real service also exposes a return-label toggle (real FedEx return vs
// forward label, on by default) and every plan a pickup mode (scheduled pickup
// vs drop-off). Saving persists the choice as carrier preferences; the backend
// builder uses them.
export function FedexPlanSelect({
  carrierId,
  preferences,
  value,
  onValueChange,
}: {
  carrierId: string;
  preferences?: {
    fedexPlan?: string | null;
    defaultServiceType?: string | null;
    incoterm?: string | null;
    useFedexReturn?: boolean | null;
    pickupType?: string | null;
  };
  // Controlled by the parent so the embedded rate viewer sees the picked
  // product instantly (before the save round-trip refetches).
  value: string;
  onValueChange: (next: string) => void;
}) {
  const t = useTranslation();
  const { editOneCarrierTransport } = useCarriersContext();
  // Real FedEx return label on by default for new carriers.
  const [fedexReturn, setFedexReturn] = useState(
    () => preferences?.useFedexReturn ?? true,
  );
  const [pickupType, setPickupType] = useState(
    () => preferences?.pickupType ?? FEDEX_PICKUP_SCHEDULED,
  );
  const [lane, setLane] = useState<FedexLane>(() => laneOf(value));
  const products = productsForLane(lane);

  const save = (next: {
    value?: string;
    fedexReturn?: boolean;
    pickupType?: string;
  }) => {
    editOneCarrierTransport({
      carrierId,
      carrier: {
        code: "fedex",
        credentials: valueToCredentials(next.value ?? value, {
          fedexReturn: next.fedexReturn ?? fedexReturn,
          pickupType: next.pickupType ?? pickupType,
        }),
      },
    });
  };

  const handleServiceChange = (next: string) => {
    onValueChange(next);
    save({ value: next });
  };

  // Switching lane resets the product to the first one of that lane, so the
  // selection (and the embedded rate viewer) always stays lane-consistent.
  const handleLaneChange = (next: FedexLane) => {
    setLane(next);
    handleServiceChange(productsForLane(next)[0].serviceType);
  };

  const handleReturnChange = (checked: boolean) => {
    setFedexReturn(checked);
    save({ fedexReturn: checked });
  };

  const handlePickupChange = (next: string) => {
    setPickupType(next);
    save({ pickupType: next });
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700">
          {t("carrier.fedexCsp.laneLabel")}
        </label>
        <ToggleGroup
          type="single"
          variant="outline"
          value={lane}
          onValueChange={(next) => {
            if (next) handleLaneChange(next as FedexLane);
          }}
          className="w-full"
        >
          <ToggleGroupItem value="domestic">
            {t("carrier.fedexCsp.laneDomestic")}
          </ToggleGroupItem>
          <ToggleGroupItem value="international">
            {t("carrier.fedexCsp.laneInternational")}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700">
          {t("carrier.fedexCsp.planLabel")}
        </label>
        <Select value={value} onValueChange={handleServiceChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {products.map((s) => (
              <SelectItem key={s.serviceType} value={s.serviceType}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500">
          {t("carrier.fedexCsp.planHelper")}
        </p>
      </div>

      {value !== FEDEX_ONE_RATE_VALUE && (
        <div className="flex items-center gap-2">
          <Switch
            id="fedex-return"
            checked={fedexReturn}
            onCheckedChange={handleReturnChange}
          />
          <label htmlFor="fedex-return" className="text-sm text-gray-700">
            {t("carrier.fedexCsp.returnLabel")}
          </label>
        </div>
      )}

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700">
          {t("carrier.fedexCsp.pickupLabel")}
        </label>
        <Select value={pickupType} onValueChange={handlePickupChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={FEDEX_PICKUP_SCHEDULED}>
              {t("carrier.fedexCsp.pickupScheduled")}
            </SelectItem>
            <SelectItem value={FEDEX_PICKUP_DROPOFF}>
              {t("carrier.fedexCsp.pickupDropoff")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
